import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ODataEntitySetService } from 'angular-odata';
import { LicenseRenewalWorkflow } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { TimelineService } from './timeline.service';
import { TimelineItemMappers } from '../models/timeline-config.model';

@Injectable({
  providedIn: 'root',
})
export class RenewalTimelineAdapterService extends TimelineService {
  private reviewsService: ODataEntitySetService<LicenseRenewalWorkflow>;
  constructor() {
    super();
    this.reviewsService = this.factory.entitySet<LicenseRenewalWorkflow>(
      'LicenseRenewalWorkflows',
      'QualicoatBackend.Domain.Certification.Licenses.LicenseRenewalWorkflow'
    );
  }

  protected fetchEntityData(entityType: string, entityId: string): Observable<unknown> {
    return this.reviewsService.entities().entity(entityId).fetch({
      fetchPolicy: 'no-cache',
    });
  }

  getReviewTimelineMappers(): TimelineItemMappers {
    return {
      titleMapper: (eventType: string, _eventData?: Record<string, unknown>): string => {
        switch (eventType) {
          case 'license_renewal_review.created':
            return 'Renewal created';
          case 'license_renewal_review.state_changed':
            return 'State changed';
          case 'license_renewal_review.candidates_identified':
            return 'Candidates identified';
          case 'license_renewal_review.selection_confirmed':
            return 'Selection confirmed';
          case 'license_renewal_review.certificates_sent':
            return 'Certificates sent';
          case 'license_renewal_review.completed':
            return 'Review completed';
          case 'license_renewal_review.cancelled':
            return 'Review cancelled';
          case 'license_renewal_review.note_added':
            return 'Note added';
          default:
            return eventType
              .replace(/license_renewal_review\./g, '')
              .replace(/[._]/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase());
        }
      },

      contentMapper: (eventType: string, eventData?: Record<string, unknown>): string => {
        if (!eventData) return '';
        if (eventType.includes('state_changed') && eventData['from_state'] && eventData['to_state']) {
          return `${this.formatState(String(eventData['from_state']))} → ${this.formatState(String(eventData['to_state']))}`;
        }

        if (eventType.includes('candidates_identified')) {
          const count = eventData['total_candidates'] || 0;
          const eligible = eventData['eligible_candidates'] || 0;
          return `${count} candidates found, ${eligible} eligible`;
        }

        if (eventType.includes('selection_confirmed')) {
          const selected = eventData['selected_count'] || 0;
          return `${selected} licenses selected for extension`;
        }

        if (eventType.includes('certificates_sent')) {
          const sent = eventData['emails_sent'] || 0;
          return `${sent} certificate emails sent`;
        }

        if (eventType.includes('note') && eventData['note']) {
          return String(eventData['note']);
        }

        if (eventType.includes('cancelled') && eventData['reason']) {
          return `Reason: ${String(eventData['reason'])}`;
        }

        if (eventData['notes'] || eventData['review_notes']) {
          return String(eventData['notes'] || eventData['review_notes']);
        }

        return '';
      },

      iconMapper: (eventType: string): string => {
        if (eventType.includes('state_changed')) return 'arrows-turn-to-dots';
        if (eventType.includes('created')) return 'plus-circle';
        if (eventType.includes('candidates')) return 'search';
        if (eventType.includes('selection')) return 'check-square';
        if (eventType.includes('certificate')) return 'envelope';
        if (eventType.includes('completed')) return 'check-circle';
        if (eventType.includes('cancelled')) return 'times-circle';
        if (eventType.includes('note')) return 'comment';
        return 'info-circle';
      },
    };
  }

  private formatState(state: string): string {
    if (!state) return '';
    return state
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }
}
